import type { ActivitySource } from "@/shared/api/activity-source";
import { jsonFromResponse } from "@/shared/api/client";
import { authFetch, getWebDeviceId } from "@/shared/auth/session";
import { defaultBriefSequenceForCountry } from "../brief/default-sequence";
import { resolveCanonicalAudioResponse } from "./audio-response-contract.mjs";
import { localDateKey, shortClientFingerprint } from "./audio-media-utils.mjs";
import {
  canonicalBriefJourneyAudioArticles,
  canonicalBriefNarrationMatches,
  canonicalBriefNarrationTasks,
  sanitizeBriefJourneyNarrationSource,
} from "./audio-payload-parity.mjs";
import type { AudioResponse, BriefJourneySectionAudioOpts } from "./audio-types";
import { normalizedAudioVoiceId } from "./audio-voice-policy.mjs";
import {
  captureBriefRunDailyDefaultEditionAudioOffer,
  captureBriefRunSystemBudgetError,
} from "./daily-default-edition-audio";
import {
  dailyDefaultOfferForBriefRunError,
  isDailyDefaultSystemBudgetError,
} from "./daily-default-edition-audio-contract.mjs";
import { negotiateDeviceSpeechForAudioRequest } from "./device-speech-negotiation.mjs";

export const BRIEF_JOURNEY_PREPARED_AUDIO_CACHE_LIMIT = 48;
export const BRIEF_JOURNEY_PREPARATION_MAX_CONCURRENCY = 3;
export const BRIEF_JOURNEY_PREPARATION_TIMEOUT_MS = 75_000;
export const BRIEF_NARRATION_PENDING_MAX_RETRIES = 5;

const BRIEF_NARRATION_PENDING_MIN_DELAY_MS = 250;
const BRIEF_NARRATION_PENDING_MAX_DELAY_MS = 2_500;
const BRIEF_NARRATION_PENDING_RETRY_DELAYS_MS = [400, 700, 1_100, 1_600, 2_200] as const;

export type BriefJourneyPreparationRequest = {
  key: string;
  itemType: string;
  languageCode: string;
  countryCode: string;
  requestedVoiceId: string;
  body: Record<string, unknown>;
};

export type PreparedBriefJourneyAudio =
  | Readonly<{ kind: "mp3"; response: AudioResponse }>
  | Readonly<{ kind: "device-speech"; response: AudioResponse; speechText: string }>;

export type BriefJourneyPreparationResult = Readonly<{
  cancelled: boolean;
  dailyDefaultAudioOffered?: true;
  generation: number;
  preparedCount: number;
  requestedCount: number;
  status: "cancelled" | "complete" | "partial" | "system-budget" | "user-budget";
  timedOut: boolean;
}>;

type BriefNarrationPreparationResponse = AudioResponse & {
  status?: string;
  pendingReason?: string;
  retryAfterMs?: number | null;
};

export function briefNarrationPendingRetryDelayMs(
  completedRetries: number,
  serverRetryAfterMs?: number | null,
): number {
  if (typeof serverRetryAfterMs === "number" && Number.isFinite(serverRetryAfterMs)) {
    return Math.max(
      BRIEF_NARRATION_PENDING_MIN_DELAY_MS,
      Math.min(BRIEF_NARRATION_PENDING_MAX_DELAY_MS, Math.trunc(serverRetryAfterMs)),
    );
  }
  const index = Math.max(
    0,
    Math.min(BRIEF_NARRATION_PENDING_RETRY_DELAYS_MS.length - 1, Math.trunc(completedRetries)),
  );
  return BRIEF_NARRATION_PENDING_RETRY_DELAYS_MS[index];
}

export function shouldRetryPendingBriefNarration(
  responseStatus: number,
  data: BriefNarrationPreparationResponse,
  completedRetries: number,
): boolean {
  const audioContent = typeof data.audioContent === "string" ? data.audioContent.trim() : "";
  const speechText = typeof data.speechText === "string" ? data.speechText.trim() : "";
  return (
    responseStatus === 202 &&
    data.ok === true &&
    String(data.status || "")
      .trim()
      .toLowerCase() === "pending" &&
    String(data.pendingReason || "")
      .trim()
      .toLowerCase() === "generation_in_progress" &&
    !audioContent &&
    !speechText &&
    completedRetries < BRIEF_NARRATION_PENDING_MAX_RETRIES
  );
}

function waitForBriefNarrationPendingRetry(delayMs: number, signal: AbortSignal): Promise<boolean> {
  if (signal.aborted) return Promise.resolve(false);
  return new Promise((resolve) => {
    let settled = false;
    let timeout: ReturnType<typeof globalThis.setTimeout> | null = null;
    const finish = (completed: boolean) => {
      if (settled) return;
      settled = true;
      if (timeout != null) globalThis.clearTimeout(timeout);
      signal.removeEventListener("abort", onAbort);
      resolve(completed);
    };
    const onAbort = () => finish(false);
    signal.addEventListener("abort", onAbort, { once: true });
    timeout = globalThis.setTimeout(() => finish(true), delayMs);
  });
}

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function copiedRecords(value: unknown): Array<Record<string, unknown>> {
  return Array.isArray(value)
    ? value
        .map(record)
        .filter((item): item is Record<string, unknown> => item != null)
        .map((item) => ({ ...item }))
    : [];
}

/** Snapshot only canonical public-capable facts; private/custom ids are filtered again at playback. */
function dailyDefaultFallbackRequestBody(
  requests: BriefJourneyPreparationRequest[],
): Record<string, unknown> {
  const first = requests[0];
  const sections: Array<Record<string, unknown>> = [];
  const seen = new Set<string>();
  const publicSectionIds = new Set(
    defaultBriefSequenceForCountry(first?.countryCode).filter(
      (id) => id !== "watchlist" && id !== "world_affairs",
    ),
  );
  for (const request of requests) {
    if (
      request.countryCode !== first?.countryCode ||
      request.languageCode !== first?.languageCode
    ) {
      continue;
    }
    const transcript = record(request.body.transcriptContext);
    const id = String(transcript?.sectionId || "").trim();
    if (!publicSectionIds.has(id) || seen.has(id)) continue;
    seen.add(id);
    const articles = copiedRecords(transcript?.articles);
    const narrationMatches = copiedRecords(transcript?.narrationMatches);
    sections.push({
      id,
      title: String(transcript?.title || "").trim(),
      body: String(transcript?.body || transcript?.summary || "").trim(),
      ...(articles.length > 0 ? { articles } : {}),
      ...(narrationMatches.length > 0 ? { narrationMatches } : {}),
    });
  }
  return {
    country: first?.countryCode || "",
    lang: first?.languageCode || "",
    facts: { briefSections: sections },
  };
}

function narrationSportForSectionId(sectionId: string): string {
  const id = String(sectionId || "")
    .trim()
    .toLowerCase();
  if (id === "cricket") return "cricket";
  if (!id.startsWith("sport_")) return "";
  const sport = id.slice("sport_".length);
  return sport === "soccer" ? "football" : sport;
}

export function briefJourneyRequestBody(
  opts: BriefJourneySectionAudioOpts,
): Record<string, unknown> {
  const summary = sanitizeBriefJourneyNarrationSource(opts.sectionId, opts.itemType, opts.summary);
  const hasCanonicalMatches = opts.narrationMatches !== undefined;
  const narrationSport = narrationSportForSectionId(opts.sectionId);
  return {
    mode: "run",
    scope: opts.scope,
    countryCode: opts.countryCode,
    languageCode: opts.languageCode,
    voiceId: normalizedAudioVoiceId(opts.voiceId),
    userName: opts.userName || "",
    transcriptContext: {
      sectionId: opts.sectionId,
      title: opts.sectionTitle,
      summary,
      body: summary,
      ...(opts.quoteText ? { quoteText: opts.quoteText } : {}),
      ...(opts.quoteAuthor ? { quoteAuthor: opts.quoteAuthor } : {}),
      sections: [{ id: opts.sectionId, title: opts.sectionTitle, summary, body: summary }],
      articles: canonicalBriefJourneyAudioArticles(opts.articles || []),
      tasks: canonicalBriefNarrationTasks(opts.tasks || []),
      ...(hasCanonicalMatches && narrationSport ? { narrationSport } : {}),
      ...(hasCanonicalMatches
        ? {
            narrationMatches: canonicalBriefNarrationMatches(opts.narrationMatches || []),
          }
        : { matches: canonicalBriefNarrationMatches(opts.matches || []) }),
    },
  };
}

export function briefJourneySectionAudioKey(opts: BriefJourneySectionAudioOpts): string {
  const fingerprint = shortClientFingerprint(
    JSON.stringify({
      itemType: opts.itemType,
      sectionId: opts.sectionId,
      voiceId: opts.voiceId,
      body: briefJourneyRequestBody(opts),
    }),
  );
  return `brief-section:${opts.sectionId}:${fingerprint}`;
}

function briefJourneyPreparationRequest(
  opts: BriefJourneySectionAudioOpts,
): BriefJourneyPreparationRequest {
  return {
    key: briefJourneySectionAudioKey(opts),
    itemType: opts.itemType,
    languageCode: opts.languageCode,
    countryCode: opts.countryCode,
    requestedVoiceId: normalizedAudioVoiceId(opts.voiceId),
    body: briefJourneyRequestBody(opts),
  };
}

const preparedAudio = new Map<string, PreparedBriefJourneyAudio>();
let preparationGeneration = 0;
let preparationController: AbortController | null = null;
let activeDeckKeys = new Set<string>();

function rememberPreparedAudio(key: string, value: PreparedBriefJourneyAudio) {
  preparedAudio.delete(key);
  preparedAudio.set(key, value);
  while (preparedAudio.size > BRIEF_JOURNEY_PREPARED_AUDIO_CACHE_LIMIT) {
    const oldest = preparedAudio.keys().next().value as string | undefined;
    if (!oldest) break;
    preparedAudio.delete(oldest);
  }
}

export function rememberPreparedBriefJourneyMp3(key: string, response: AudioResponse) {
  rememberPreparedAudio(key, Object.freeze({ kind: "mp3", response }));
}

export function getPreparedBriefJourneyAudio(key: string): PreparedBriefJourneyAudio | undefined {
  const prepared = preparedAudio.get(key);
  if (!prepared) return undefined;
  preparedAudio.delete(key);
  preparedAudio.set(key, prepared);
  return prepared;
}

export function cancelBriefJourneyAudioPreparation(discardPreparedDeck = true) {
  preparationGeneration += 1;
  preparationController?.abort();
  preparationController = null;
  if (discardPreparedDeck) {
    for (const key of activeDeckKeys) preparedAudio.delete(key);
  }
  activeDeckKeys = new Set();
}

function requestPath(itemType: string): string {
  return `/api/brief/narration/item/${encodeURIComponent(itemType)}/generate`;
}

function capabilityKey(request: BriefJourneyPreparationRequest): string {
  return [
    normalizedAudioVoiceId(request.requestedVoiceId),
    request.languageCode,
    request.countryCode,
  ].join("|");
}

async function prepareRequest(
  request: BriefJourneyPreparationRequest,
  generation: number,
  controller: AbortController,
  deviceSpeechAvailability: Map<string, Promise<boolean>>,
  activitySource: ActivitySource,
): Promise<boolean> {
  if (getPreparedBriefJourneyAudio(request.key)) return true;
  const requestedVoiceId = normalizedAudioVoiceId(request.requestedVoiceId);
  const availabilityKey = capabilityKey(request);
  let availability = deviceSpeechAvailability.get(availabilityKey);
  if (!availability) {
    availability = (async () => {
      if (requestedVoiceId !== "auto") return false;
      const result = await negotiateDeviceSpeechForAudioRequest({
        path: requestPath(request.itemType),
        requestedVoiceId,
        languageCode: request.languageCode,
        countryCode: request.countryCode,
        signal: controller.signal,
      });
      return result?.availability === "available";
    })();
    deviceSpeechAvailability.set(availabilityKey, availability);
  }
  const deviceSpeechAuthorized = await availability;
  if (
    controller.signal.aborted ||
    generation !== preparationGeneration ||
    preparationController !== controller
  ) {
    return false;
  }
  const path = requestPath(request.itemType);
  const body = JSON.stringify({
    clientDeviceId: getWebDeviceId(),
    clientLocalDate: localDateKey(),
    requestPurpose: "run-preparation",
    ...request.body,
    ...(deviceSpeechAuthorized ? { clientCapabilities: { deviceSpeechFallback: true } } : {}),
    voiceId: requestedVoiceId,
  });
  let completedRetries = 0;
  while (true) {
    const response = await authFetch(path, {
      activitySource,
      method: "POST",
      signal: controller.signal,
      body,
    });
    const data = await jsonFromResponse<BriefNarrationPreparationResponse>(response, {
      fallbackCode: "brief_narration_unavailable",
    });
    if (
      controller.signal.aborted ||
      generation !== preparationGeneration ||
      preparationController !== controller
    ) {
      return false;
    }
    const resolution = resolveCanonicalAudioResponse(data, {
      requestedVoiceId,
      deviceSpeechAuthorized,
    });
    if (resolution.kind === "mp3") {
      rememberPreparedBriefJourneyMp3(request.key, data);
      return true;
    }
    if (resolution.kind === "device-speech") {
      rememberPreparedAudio(
        request.key,
        Object.freeze({
          kind: "device-speech",
          response: data,
          speechText: resolution.speechText,
        }),
      );
      return true;
    }
    if (!shouldRetryPendingBriefNarration(response.status, data, completedRetries)) return false;
    const waited = await waitForBriefNarrationPendingRetry(
      briefNarrationPendingRetryDelayMs(completedRetries, data.retryAfterMs),
      controller.signal,
    );
    if (
      !waited ||
      controller.signal.aborted ||
      generation !== preparationGeneration ||
      preparationController !== controller
    ) {
      return false;
    }
    completedRetries += 1;
  }
}

export async function prepareBriefJourneyAudioDeck(
  requests: BriefJourneyPreparationRequest[],
  activitySource: ActivitySource = "unknown",
): Promise<BriefJourneyPreparationResult> {
  const uniqueRequests = [
    ...new Map(
      requests
        .filter((request) => request.key && request.itemType)
        .map((request) => [request.key, request]),
    ).values(),
  ];
  const nextDeckKeys = new Set(uniqueRequests.map((request) => request.key));
  preparationGeneration += 1;
  preparationController?.abort();
  for (const key of activeDeckKeys) {
    if (!nextDeckKeys.has(key)) preparedAudio.delete(key);
  }
  const generation = preparationGeneration;
  const controller = new AbortController();
  preparationController = controller;
  activeDeckKeys = nextDeckKeys;
  if (uniqueRequests.length === 0) {
    preparationController = null;
    return Object.freeze({
      cancelled: false,
      generation,
      preparedCount: 0,
      requestedCount: 0,
      status: "complete",
      timedOut: false,
    });
  }

  const deviceSpeechAvailability = new Map<string, Promise<boolean>>();
  const fallbackRequestBody = dailyDefaultFallbackRequestBody(uniqueRequests);
  let nextIndex = 0;
  let preparedCount = 0;
  let timedOut = false;
  const terminalBudget: {
    current: Readonly<{ error: unknown; kind: "system" | "user" }> | null;
  } = { current: null };
  const timeout = globalThis.setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, BRIEF_JOURNEY_PREPARATION_TIMEOUT_MS);
  const worker = async () => {
    while (
      !controller.signal.aborted &&
      generation === preparationGeneration &&
      preparationController === controller
    ) {
      const index = nextIndex;
      nextIndex += 1;
      if (index >= uniqueRequests.length) return;
      try {
        if (
          await prepareRequest(
            uniqueRequests[index],
            generation,
            controller,
            deviceSpeechAvailability,
            activitySource,
          )
        ) {
          preparedCount += 1;
        }
      } catch (error) {
        if (isDailyDefaultSystemBudgetError(error)) {
          terminalBudget.current = Object.freeze({ error, kind: "system" });
          controller.abort();
          return;
        }
        if (
          !terminalBudget.current &&
          dailyDefaultOfferForBriefRunError({ error, requestBody: fallbackRequestBody })
        ) {
          terminalBudget.current = Object.freeze({ error, kind: "user" });
          controller.abort();
          return;
        }
        if (controller.signal.aborted) return;
        // Individual failures settle the bounded worker. The preparation UI
        // remains fail-closed and owns an explicit retry for an incomplete deck.
      }
    }
  };
  try {
    await Promise.all(
      Array.from(
        {
          length: Math.min(BRIEF_JOURNEY_PREPARATION_MAX_CONCURRENCY, uniqueRequests.length),
        },
        () => worker(),
      ),
    );
  } finally {
    globalThis.clearTimeout(timeout);
  }
  const ownsPreparation =
    generation === preparationGeneration && preparationController === controller;
  const settledBudget = terminalBudget.current;
  const budgetTerminated = !timedOut && settledBudget != null && ownsPreparation;
  const cancelled =
    !timedOut && !budgetTerminated && (controller.signal.aborted || !ownsPreparation);
  if (!cancelled) preparationController = null;
  const dailyDefaultAudioOffered =
    budgetTerminated &&
    settledBudget?.kind === "user" &&
    captureBriefRunDailyDefaultEditionAudioOffer(settledBudget.error, fallbackRequestBody);
  if (budgetTerminated && settledBudget?.kind === "system") {
    captureBriefRunSystemBudgetError(settledBudget.error, fallbackRequestBody);
  }
  return Object.freeze({
    cancelled,
    ...(dailyDefaultAudioOffered ? { dailyDefaultAudioOffered: true as const } : {}),
    generation,
    preparedCount,
    requestedCount: uniqueRequests.length,
    status: cancelled
      ? "cancelled"
      : budgetTerminated && settledBudget?.kind === "user"
        ? "user-budget"
        : budgetTerminated && settledBudget?.kind === "system"
          ? "system-budget"
          : !timedOut && preparedCount === uniqueRequests.length
            ? "complete"
            : "partial",
    timedOut,
  });
}

export function prepareBriefJourneyAudioSections(
  sections: BriefJourneySectionAudioOpts[],
  activitySource: ActivitySource = "unknown",
) {
  return prepareBriefJourneyAudioDeck(sections.map(briefJourneyPreparationRequest), activitySource);
}
